import React from 'react';
import { authFetch } from 'utils/auth-fetch';
import { isInIframe } from 'utils/iframe';
/**
 * Resolves a protected resource URL to a value usable in `src`/`data`
 * attributes of media, object and iframe elements.
 *
 * Outside an iFrame the URL is returned unchanged: such requests are same-site,
 * so the browser attaches the session cookie.
 *
 * In iFrame context (embedded in WebUntis) the SameSite=Lax session cookie is
 * not sent on cross-site subresource requests, and browser-initiated element
 * loads cannot carry the Bearer token. The resource is therefore fetched via
 * `authFetch` and exposed as a `blob:` URL, which is revoked on cleanup.
 *
 * @param resourceUrl URL of the protected resource.
 * @param enabled Set to `false` to skip resolving; no request is made and `url` stays undefined.
 * @returns Resolution state; `url` is set once the resource is usable.
 */
export const useAuthResourceUrl = (resourceUrl, enabled = true) => {
    const needsBlob = enabled && isInIframe();
    const [state, setState] = React.useState({
        error: false,
        loading: needsBlob,
        url: enabled && !needsBlob ? resourceUrl : undefined,
    });
    React.useEffect(() => {
        if (!needsBlob) {
            setState({
                error: false,
                loading: false,
                url: enabled ? resourceUrl : undefined,
            });
            return undefined;
        }
        let objectUrl = null;
        let cancelled = false;
        setState({ error: false, loading: true, url: undefined });
        authFetch(resourceUrl)
            .then(async (response) => {
            if (!response.ok) {
                throw new Error(`Failed to load resource: ${response.status}`);
            }
            return response.blob();
        })
            .then((blob) => {
            if (cancelled) {
                return;
            }
            objectUrl = URL.createObjectURL(blob);
            setState({ error: false, loading: false, url: objectUrl });
        })
            .catch(() => {
            if (!cancelled) {
                setState({ error: true, loading: false, url: undefined });
            }
        });
        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [enabled, needsBlob, resourceUrl]);
    return state;
};
